import { useEffect, useRef } from 'react';

/**
 * Shared IntersectionObserver — one instance per page, many consumers.
 *
 * Why this shape: per-component observers are cheap individually but
 * allocate at O(n) elements. A single observer with a WeakMap callback
 * registry is the standard production pattern and is measurably lighter
 * on devices with tight main threads.
 */
type RevealCallback = (entry: IntersectionObserverEntry) => void;

let shared: IntersectionObserver | null = null;
const callbacks = new WeakMap<Element, RevealCallback>();

function getObserver(): IntersectionObserver | null {
  if (shared) return shared;
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return null;
  shared = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const cb = callbacks.get(entry.target);
        if (cb) cb(entry);
        shared?.unobserve(entry.target);
        callbacks.delete(entry.target);
      }
    },
    { threshold: 0.15, rootMargin: '0px 0px -60px 0px' },
  );
  return shared;
}

/**
 * Attach the returned ref to the element you want to reveal. The element
 * gets `.in` added to its classList when it first enters the viewport.
 * The generic param lets callers narrow to e.g. HTMLHeadingElement.
 */
export default function useReveal<T extends HTMLElement = HTMLElement>(
  onReveal?: RevealCallback,
) {
  // Return type is inferred: React 18 gives RefObject<T>, React 19 gives
  // RefObject<T | null>. Either one is assignable to a JSX `ref` prop, and
  // letting the inference stand avoids version-specific annotations.
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = getObserver();
    if (!io) {
      // SSR / no-IO fallback: reveal immediately so content isn't stuck hidden.
      el.classList.add('in');
      return;
    }
    callbacks.set(el, (entry) => {
      el.classList.add('in');
      onReveal?.(entry);
    });
    io.observe(el);
    return () => {
      callbacks.delete(el);
      io.unobserve(el);
    };
  }, [onReveal]);

  return ref;
}
